import { useEffect, useState } from "react";
import { api, formatMoney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/constants/testIds";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Reusable stats surface.
 *   compact=false → full page: KPI grid + monthly bars + best/worst
 *   compact=true  → KPI strip only (4 numbers, mini)
 *   expandable=true → renders an Expand/Collapse toggle; parent controls initial state via `defaultExpanded`
 */
export function StatsPanel({ year, expandable = false, defaultExpanded = false }) {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/accounts");
        setAccounts((data || []).filter((a) => a.status !== "archived"));
      } catch (_e) { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const q = accountId && accountId !== "all" ? `&account_id=${accountId}` : "";
        const { data } = await api.get(`/journal/stats?year=${year}${q}`);
        setStats(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [year, accountId]);

  if (loading || !stats) {
    return (
      <div className="text-center text-[#A39A8E] py-6 font-mono text-xs">
        Loading stats…
      </div>
    );
  }

  const currency = user?.currency;
  const maxAbs = Math.max(1, ...Object.values(stats.monthly).map((m) => Math.abs(m.pnl)));

  return (
    <section data-testid="stats-panel" className="border border-[#2E2A25] bg-[#1A1815] rounded-sm">
      {/* Account filter — only shown when user has accounts */}
      {accounts.length > 0 && (
        <div className="flex items-center gap-3 px-4 pt-3 flex-wrap">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E]">Account:</span>
          <select
            data-testid="stats-account-filter"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            className="bg-[#0F0E0C] border border-[#2E2A25] text-[#EBE5D9] px-2.5 py-1 text-xs font-mono focus:outline-none focus:border-[#CFA66A] rounded-sm"
          >
            <option value="all">All accounts</option>
            <option value="unassigned">Unassigned trades</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
        </div>
      )}

      {/* KPI strip — always visible */}
      <div className="flex items-center justify-between p-4 gap-4 flex-wrap">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1 min-w-0">
          <Kpi
            label={`${year} P&L`}
            value={formatMoney(stats.total_pnl, currency)}
            tid={TID.statTotalPnl}
            cls={
              stats.total_pnl > 0
                ? "text-[#7A9E80]"
                : stats.total_pnl < 0
                  ? "text-[#B56258]"
                  : "text-[#EBE5D9]"
            }
          />
          <Kpi label="Win rate" value={`${stats.win_rate}%`} tid={TID.statWinRate} />
          <Kpi label="Trades" value={stats.trade_count} tid={TID.statTrades} />
          <Kpi label="Days journaled" value={stats.days_journaled} tid={TID.statDays} />
        </div>
        {expandable && (
          <button
            data-testid="stats-panel-toggle"
            onClick={() => setExpanded((e) => !e)}
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#CFA66A] hover:text-[#EBE5D9] transition-colors shrink-0"
          >
            {expanded ? "▾ Collapse" : "▸ Expand"}
          </button>
        )}
      </div>

      {(!expandable || expanded) && (
        <div data-testid="stats-panel-expanded" className="border-t border-[#2E2A25] p-4 space-y-4">
          {/* Monthly bars */}
          <div>
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="font-head text-base text-[#EBE5D9]">Monthly P&amp;L</h2>
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E]">
                12 months
              </span>
            </div>
            <div className="grid grid-cols-12 gap-2 h-32 sm:h-40 items-end">
              {MONTHS.map((m, i) => {
                const key = String(i + 1).padStart(2, "0");
                const row = stats.monthly[key];
                const heightPct = Math.min(100, (Math.abs(row.pnl) / maxAbs) * 100);
                const isProfit = row.pnl > 0;
                const isLoss = row.pnl < 0;
                return (
                  <div key={m} className="flex flex-col items-center h-full justify-end">
                    <span
                      className={`font-mono text-[8px] mb-1 ${
                        isProfit
                          ? "text-[#7A9E80]"
                          : isLoss
                            ? "text-[#B56258]"
                            : "text-[#A39A8E]"
                      }`}
                    >
                      {row.pnl !== 0 ? formatMoney(row.pnl, currency).replace(/\.\d{2}$/, "") : ""}
                    </span>
                    <div
                      className={`w-full transition-all ${
                        isProfit
                          ? "bg-[#5E7A62]/40 border-t border-[#5E7A62]"
                          : isLoss
                            ? "bg-[#8C4A42]/40 border-t border-[#8C4A42]"
                            : "bg-[#2E2A25]"
                      }`}
                      style={{ height: `${heightPct}%`, minHeight: row.pnl !== 0 ? "3px" : "1px" }}
                    />
                    <span className="font-mono text-[9px] text-[#A39A8E] mt-1.5">{m}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Best / Worst */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <BestWorstCard tone="best" pnl={stats.best_day.pnl} date={stats.best_day.date} currency={currency} />
            <BestWorstCard tone="worst" pnl={stats.worst_day.pnl} date={stats.worst_day.date} currency={currency} />
          </div>
        </div>
      )}
    </section>
  );
}

function Kpi({ label, value, tid, cls = "text-[#EBE5D9]" }) {
  return (
    <div data-testid={tid} className="min-w-0">
      <p className="font-mono text-[9px] uppercase tracking-widest text-[#A39A8E]">{label}</p>
      <p className={`font-mono text-lg sm:text-xl font-medium mt-0.5 truncate ${cls}`}>{value}</p>
    </div>
  );
}

function BestWorstCard({ tone, pnl, date, currency }) {
  const isBest = tone === "best";
  return (
    <div
      className={`border p-4 rounded-sm ${
        isBest ? "border-[#5E7A62]/30 bg-[#5E7A62]/5" : "border-[#8C4A42]/30 bg-[#8C4A42]/5"
      }`}
    >
      <p className={`font-mono text-[10px] uppercase tracking-[0.3em] ${isBest ? "text-[#7A9E80]" : "text-[#B56258]"}`}>
        — {isBest ? "Best day" : "Worst day"}
      </p>
      <p className={`font-mono text-2xl mt-1 ${isBest ? "text-[#7A9E80]" : "text-[#B56258]"}`}>
        {formatMoney(pnl, currency)}
      </p>
      <p className="text-xs text-[#A39A8E] mt-0.5">
        {date || (isBest ? "No profitable day yet" : "No losing day yet")}
      </p>
    </div>
  );
}
